import type { ReactNode } from 'react';
import type { DnaProfileRecord } from '../lib/api';
import { Card } from './Card';
import styles from './SynthesisPanel.module.css';

// =============================================================================
// SynthesisPanel — the DNA reveal: through-line / positioning spine / origins
// / drivers (LANE DNA-BUILD, Phase 0 + Phase 1). Re-skinned port of apps/web/
// src/pages/Dna.tsx's inline SynthesisPanel fn (:514-563). Renders the SAME
// four fields Refine's Phase-0 "Threads Meira is noticing" card already reads
// off dna.state.overlay.profile — this panel is the fuller reveal shown once
// a session reaches synthesis/complete, or on the standing Refine summary.
//
// career-dna.md export (Dna.tsx:543-548, GET /dna/profile/export) is Phase 3
// per the plan — deferred, not ported here.
//
// NO INVENTION: every section renders only when the field is non-null/
// non-empty; a profile with a thin field shows less, never an invented
// filler sentence.
// =============================================================================

export interface SynthesisPanelProps {
  busy: boolean;
  profile: DnaProfileRecord | null;
  onReveal: () => void;
}

export function SynthesisPanel({ busy, profile, onReveal }: SynthesisPanelProps) {
  if (!profile) {
    return (
      <Card className={styles.revealCard}>
        <p className={styles.revealPrompt}>You've covered the ground. Ready to see the pattern?</p>
        <button type="button" onClick={onReveal} disabled={busy} className={styles.revealButton}>
          {busy ? (
            <svg
              className={styles.spinner}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="42 14"
              />
            </svg>
          ) : (
            <SparkIcon />
          )}
          Reveal your DNA
        </button>
      </Card>
    );
  }

  const hasAny =
    profile.throughLine || profile.positioningSpine || profile.origins || profile.drivers;

  if (!hasAny) {
    return (
      <Card className={styles.revealCard}>
        <p className={styles.revealPrompt}>
          Nothing came back from that pass yet — keep talking and try again.
        </p>
      </Card>
    );
  }

  return (
    <Card className={styles.panel}>
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Your DNA</h2>
      </div>
      {profile.throughLine ? (
        <Section title="The through-line">{profile.throughLine}</Section>
      ) : null}
      {profile.positioningSpine ? (
        <div className={styles.spineBox}>
          <div className={styles.spineLabel}>Positioning spine</div>
          <p className={styles.spineText}>{profile.positioningSpine}</p>
        </div>
      ) : null}
      {profile.origins ? <Section title="Origins">{profile.origins}</Section> : null}
      {profile.drivers ? <Section title="What drives you">{profile.drivers}</Section> : null}
    </Card>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className={styles.sectionTitle}>{title}</div>
      <p className={styles.sectionBody}>{children}</p>
    </div>
  );
}

function SparkIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l2.4 7.2H22l-6 4.4 2.3 7.2L12 16.4l-6.3 4.4 2.3-7.2-6-4.4h7.6z" />
    </svg>
  );
}
